import { getTripCoords, haversineDistance, sumDeadheadMilesForTotal } from './deadheadEta';
import { calculateTripFreeTimeInBucket } from './tripFreeTime';
import { groupTripsByWindow, isFleetDriver } from './fleetWindows';
import { formatTripRevenue, sumTripRevenue } from './tripRevenue';

export const LINEUP_EMPTY = '—';

const IN_PROGRESS_STATUSES = new Set([
  'onway',
  'on way',
  'arrived',
  'started',
  'pickedup',
  'picked up',
  'inprogress',
  'in progress',
  'in_progress',
]);

function isBreakTrip(trip) {
  return Boolean(trip?.is_break || trip?.isBreakTrip);
}

/** Active trip: onway, arrived, started, or the sequence-0 busy card. */
function isInProgressTrip(trip) {
  if (!trip || isBreakTrip(trip)) return false;
  if (trip.is_busy_trip === true || trip.sequence === 0) return true;
  const status = String(trip.trip_status || trip.status || '').trim().toLowerCase();
  return IN_PROGRESS_STATUSES.has(status);
}

/** Will-call legs store 23:59 when no real schedule exists. */
function isEndOfDayPlaceholder(iso) {
  return typeof iso === 'string' && /T23:59/.test(iso.trim());
}

function hasRealTrip(driver) {
  return (driver?.trips || []).some((trip) => !isBreakTrip(trip));
}

function shiftInstant(iso) {
  if (iso == null || iso === '') return null;
  const time = new Date(iso).getTime();
  return Number.isNaN(time) ? null : time;
}

/**
 * Portal shift clock as stored. The ISO string is already in the company offset
 * (for example 00:00-04:00). Do not project that instant into the browser timezone.
 */
export function formatShiftClock(iso) {
  if (iso == null || iso === '') return LINEUP_EMPTY;
  const text = String(iso).trim();
  const fromIso = text.match(/T(\d{2}):(\d{2})/);
  if (fromIso) return `${fromIso[1]}:${fromIso[2]}`;
  const plain = text.match(/^(\d{1,2}):(\d{2})/);
  if (plain) return `${plain[1].padStart(2, '0')}:${plain[2]}`;
  return LINEUP_EMPTY;
}

function dropoffEstimateIso(trip) {
  return trip?.trip_details?.approximate_dropoff_time
    || trip?.approximate_dropoff_time
    || trip?.appointment_time
    || null;
}

function etaToPickupMinutes(trip) {
  const minutes = Number(trip?.eta_to_pickup_minutes ?? trip?.etaToPickupMinutes);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
}

function unloadMinutes(trip) {
  const minutes = Number(
    trip?.trip_details?.unload_time_minutes ?? trip?.unloadTime ?? trip?.unload_time
  );
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
}

function realTripsInOrder(driver) {
  return [...(driver?.trips || [])]
    .filter((trip) => trip && !isBreakTrip(trip))
    .sort((a, b) => (a.sequence ?? 999) - (b.sequence ?? 999));
}

/**
 * When the driver is already on the trip, schedule is often 23:59.
 * Recover "now" from the live dropoff estimate and minutes until available.
 */
function inProgressNowMs(trip) {
  const dropoffMs = shiftInstant(
    trip?.trip_details?.approximate_dropoff_time || trip?.approximate_dropoff_time || null
  );
  const availableIn = Number(trip?.available_in_minutes);
  if (dropoffMs == null || !Number.isFinite(availableIn) || availableIn <= 0) return null;
  return dropoffMs + unloadMinutes(trip) * 60000 - availableIn * 60000;
}

/** Start of one trip's work. Skips a 23:59 placeholder on an active trip. */
function workStartMs(trip) {
  if (!trip) return null;
  if (isInProgressTrip(trip)) {
    const nowMs = inProgressNowMs(trip);
    if (nowMs != null) return nowMs;
  }
  const approxMs = shiftInstant(
    trip?.trip_details?.approximate_pickup_time || trip?.approximate_pickup_time || null
  );
  if (approxMs != null) return approxMs - etaToPickupMinutes(trip) * 60000;
  const schedule = trip?.schedule_time;
  if (!schedule || isEndOfDayPlaceholder(schedule)) return null;
  const scheduleMs = shiftInstant(schedule);
  if (scheduleMs == null) return null;
  return scheduleMs - etaToPickupMinutes(trip) * 60000;
}

/** "1 hr 37 min", "5 hrs", "14 hrs 52 min", "45 min". */
export function formatWorkingHours(totalMinutes) {
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return LINEUP_EMPTY;
  const rounded = Math.max(0, Math.round(totalMinutes));
  const hours = Math.floor(rounded / 60);
  const minutes = rounded % 60;
  const hourLabel = hours === 1 ? '1 hr' : `${hours.toLocaleString('en-US')} hrs`;
  const minuteLabel = minutes === 1 ? '1 min' : `${minutes} min`;
  if (hours > 0 && minutes > 0) return `${hourLabel} ${minuteLabel}`;
  if (hours > 0) return hourLabel;
  return minuteLabel;
}

/**
 * Working window for one driver.
 * Start: active trip "now", otherwise the first real estimated pickup minus ETA.
 * End: last trip estimated dropoff plus unload minutes.
 * A 23:59 will-call placeholder on an onway or started trip is not used as the start.
 */
export function workingShiftForDriver(driver) {
  const trips = realTripsInOrder(driver);
  const last = trips[trips.length - 1];

  let startMs = null;
  for (const trip of trips) {
    const candidate = workStartMs(trip);
    if (candidate != null) {
      startMs = candidate;
      break;
    }
  }

  let endMs = null;
  if (last) {
    const dropoffMs = shiftInstant(dropoffEstimateIso(last));
    if (dropoffMs != null) endMs = dropoffMs + unloadMinutes(last) * 60000;
  }

  const minutes = startMs != null && endMs != null && endMs >= startMs
    ? Math.round((Math.floor(endMs / 60000) - Math.floor(startMs / 60000)))
    : null;

  return { label: formatWorkingHours(minutes), minutes };
}

/** Sum of free-time gaps on this driver's trips, same rules as the trip banners. */
export function freeMinutesForDriver(driver, multiloadRoutes = null) {
  const trips = driver?.trips || [];
  return trips.reduce((sum, trip) => {
    if (!trip) return sum;
    let result = null;
    try {
      result = calculateTripFreeTimeInBucket(trip, trips, multiloadRoutes);
    } catch {
      return sum;
    }
    const minutes = Number(result?.freeMinutes);
    return Number.isFinite(minutes) && minutes > 0 ? sum + minutes : sum;
  }, 0);
}

/**
 * Working shift with free-time gaps removed.
 * Updates from the current trip list, including after a manual move.
 */
export function busyShiftForDriver(driver, multiloadRoutes = null) {
  const working = workingShiftForDriver(driver);
  if (!Number.isFinite(working.minutes)) return { label: LINEUP_EMPTY, minutes: null };
  const minutes = Math.max(0, working.minutes - freeMinutesForDriver(driver, multiloadRoutes));
  return { label: formatWorkingHours(minutes), minutes };
}

function shiftDurationMinutes(startIso, endIso) {
  const start = shiftInstant(startIso);
  const end = shiftInstant(endIso);
  if (start == null || end == null || end < start) return null;
  return Math.round((end - start) / 60000);
}

/** Hours and minutes, with a thousands separator once the hour count is large. */
export function formatDurationFromMinutes(totalMinutes) {
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return LINEUP_EMPTY;
  const rounded = Math.round(totalMinutes);
  const hours = Math.floor(rounded / 60);
  const minutes = rounded % 60;
  const hoursLabel = hours.toLocaleString('en-US');
  if (hours > 0 && minutes > 0) return `${hoursLabel}h ${minutes}m`;
  if (hours > 0) return `${hoursLabel}h`;
  return `${minutes}m`;
}

/** Shift end minus shift start, rounded to the nearest minute. */
export function formatShiftDuration(startIso, endIso) {
  const minutes = shiftDurationMinutes(startIso, endIso);
  if (minutes == null) return LINEUP_EMPTY;
  return formatDurationFromMinutes(minutes);
}

function formatGroupedMiles(miles) {
  const value = roundMiles(miles);
  const text = value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `${text} mi`;
}

/** Column sums for the lineup header. Shift skips rows with no usable window. */
export function lineupColumnTotals(rows) {
  const list = rows || [];
  let shiftMinutes = 0;
  let hasShift = false;
  let loaded = 0;
  let deadhead = 0;
  let away = 0;
  let total = 0;
  let workingMinutes = 0;
  let hasWorking = false;
  let busyMinutes = 0;
  let hasBusy = false;
  let revenue = 0;
  let hasRevenue = false;
  list.forEach((row) => {
    if (Number.isFinite(row.shiftMinutes)) {
      shiftMinutes += row.shiftMinutes;
      hasShift = true;
    }
    if (Number.isFinite(row.workingMinutes)) {
      workingMinutes += row.workingMinutes;
      hasWorking = true;
    }
    if (Number.isFinite(row.busyMinutes)) {
      busyMinutes += row.busyMinutes;
      hasBusy = true;
    }
    if (row.revenue != null) {
      revenue += row.revenue;
      hasRevenue = true;
    }
    loaded += Number(row.loaded) || 0;
    deadhead += Number(row.deadhead) || 0;
    away += Number(row.away) || 0;
    total += Number(row.total) || 0;
  });
  return {
    shift: hasShift ? formatDurationFromMinutes(shiftMinutes) : LINEUP_EMPTY,
    working: hasWorking ? formatWorkingHours(workingMinutes) : LINEUP_EMPTY,
    busy: hasBusy ? formatWorkingHours(busyMinutes) : LINEUP_EMPTY,
    loaded: formatGroupedMiles(loaded),
    deadhead: formatGroupedMiles(deadhead),
    away: formatGroupedMiles(away),
    total: formatGroupedMiles(total),
    revenue: hasRevenue ? formatTripRevenue(Math.round(revenue * 100) / 100) : LINEUP_EMPTY,
  };
}

function roundMiles(miles) {
  const value = Number.isFinite(miles) ? miles : 0;
  return Number(value.toFixed(1));
}

export function formatLineupMiles(miles) {
  return `${roundMiles(miles).toFixed(1)} mi`;
}

export function driverLosLabel(driver) {
  if (driver?.fleet_window_label) return driver.fleet_window_label;
  const los = String(driver?.fleet_los_title || '').trim();
  const spaceType = String(driver?.fleet_space_type_title || '').trim();
  if (los && spaceType) return `${los} (${spaceType})`;
  if (los) return los;
  if (spaceType) return spaceType;
  return LINEUP_EMPTY;
}

function fleetWindowLosLabel(window) {
  const rig = String(window?.rig_no || window?.fleet_id || '').trim();
  const los = String(window?.los_title || '').trim();
  const parts = [rig, los].filter(Boolean);
  return parts.length ? parts.join(' ') : LINEUP_EMPTY;
}

function windowPseudoDriver(driver, window, trips) {
  const hasReturn = window?.return_latitude != null && window?.return_longitude != null;
  return {
    ...driver,
    driver_id: `${driver.driver_id}#${window.window_index}`,
    fleet_los_title: window.los_title,
    fleet_space_type_title: window.space_type_title,
    fleet_window_label: fleetWindowLosLabel(window),
    shift_window: { shift_start: window.start, shift_end: window.end, has_shift: true },
    trips,
    takeMeHome: hasReturn ? [window.return_longitude, window.return_latitude] : driver.takeMeHome,
  };
}

function expandDriversForLineup(drivers) {
  const out = [];
  for (const driver of drivers || []) {
    if (!isFleetDriver(driver)) {
      out.push(driver);
      continue;
    }
    const groups = groupTripsByWindow(driver).filter((g) => g.window && hasRealTrip({ trips: g.trips }));
    if (!groups.length) {
      out.push(driver);
      continue;
    }
    for (const group of groups) {
      out.push(windowPseudoDriver(driver, group.window, group.trips));
    }
  }
  return out;
}

function loadedMiles(driver) {
  return (driver?.trips || []).reduce((sum, trip) => {
    if (isBreakTrip(trip)) return sum;
    return sum + (Number(trip.trip_distance_miles) || Number(trip.trip_distance) || 0);
  }, 0);
}

function dropoffCoords(trip) {
  const coords = getTripCoords(trip);
  if (coords) return { lat: coords.dropoffLat, lng: coords.dropoffLng };
  const lat = Number(trip?.dropoff_latitude ?? trip?.dropoff_lat);
  const lng = Number(trip?.dropoff_longitude ?? trip?.dropoff_lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function homeCoords(driver) {
  const home = driver?.takeMeHome;
  if (!Array.isArray(home) || home.length < 2) return null;
  const lng = Number(home[0]);
  const lat = Number(home[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return { lat, lng };
}

export function awayFromHomeMiles(driver) {
  const trips = [...(driver?.trips || [])]
    .filter((trip) => !isBreakTrip(trip))
    .sort((a, b) => (a.sequence ?? 999) - (b.sequence ?? 999));
  const lastTrip = trips[trips.length - 1];
  if (!lastTrip) return 0;
  const dropoff = dropoffCoords(lastTrip);
  const home = homeCoords(driver);
  if (!dropoff || !home) return 0;
  return haversineDistance(dropoff.lat, dropoff.lng, home.lat, home.lng);
}

function compareLineupRows(a, b) {
  const aMissingLos = a.losLabel === LINEUP_EMPTY;
  const bMissingLos = b.losLabel === LINEUP_EMPTY;
  if (aMissingLos !== bMissingLos) return aMissingLos ? 1 : -1;
  if (a.losLabel !== b.losLabel) {
    return a.losLabel.localeCompare(b.losLabel, undefined, { sensitivity: 'base' });
  }
  const aMissingWork = !Number.isFinite(a.workingMinutes);
  const bMissingWork = !Number.isFinite(b.workingMinutes);
  if (aMissingWork !== bMissingWork) return aMissingWork ? 1 : -1;
  if (!aMissingWork && a.workingMinutes !== b.workingMinutes) {
    return b.workingMinutes - a.workingMinutes;
  }
  return a.driverName.localeCompare(b.driverName, undefined, { sensitivity: 'base' });
}

/**
 * Drivers with at least one non-break trip.
 * Stacked by LOS / space type. Inside each block, longest working shift first.
 * Each LOS label is one block and shares a rowspan.
 */
export function buildDriverLineup(drivers, multiloadRoutes = null) {
  const rows = expandDriversForLineup(drivers)
    .filter(hasRealTrip)
    .map((driver) => {
      const startIso = driver.shift_window?.shift_start;
      const endIso = driver.shift_window?.shift_end;
      const loaded = roundMiles(loadedMiles(driver));
      const deadhead = roundMiles(sumDeadheadMilesForTotal(driver.trips));
      const away = roundMiles(awayFromHomeMiles(driver));
      const working = workingShiftForDriver(driver);
      const busy = busyShiftForDriver(driver, multiloadRoutes);
      return {
        driverId: driver.driver_id,
        driverName: driver.driver_name || LINEUP_EMPTY,
        losLabel: driverLosLabel(driver),
        shiftStartMs: shiftInstant(startIso),
        shiftStart: formatShiftClock(startIso),
        shiftEnd: formatShiftClock(endIso),
        shiftMinutes: shiftDurationMinutes(startIso, endIso),
        shiftDuration: formatShiftDuration(startIso, endIso),
        workingShift: working.label,
        workingMinutes: working.minutes,
        busyShift: busy.label,
        busyMinutes: busy.minutes,
        revenue: sumTripRevenue(driver.trips),
        loaded,
        deadhead,
        away,
        total: roundMiles(loaded + deadhead + away),
      };
    })
    .sort(compareLineupRows);

  const groups = [];
  rows.forEach((row) => {
    const current = groups[groups.length - 1];
    if (current && current.losLabel === row.losLabel) current.rows.push(row);
    else groups.push({ losLabel: row.losLabel, rows: [row] });
  });

  return groups.flatMap((group) =>
    group.rows.map((row, index) => ({
      ...row,
      losRowSpan: index === 0 ? group.rows.length : 0,
    }))
  );
}
